import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export const fmt = (n) => new Intl.NumberFormat("en-US").format(n);

const LIVE = {
  connecting: { color: "text-zinc-500", dot: "bg-zinc-500 animate-pulse", label: "Connecting" },
  live:       { color: "text-emerald-400", dot: "bg-emerald-500 animate-pulse", label: "Live" },
  polling:    { color: "text-amber-400", dot: "bg-amber-500", label: "Polling" },
  offline:    { color: "text-zinc-600", dot: "bg-zinc-700", label: "Paused" },
};

export function LiveBadge({ state }) {
  const c = LIVE[state] || LIVE.connecting;
  return (
    <span data-testid={`queue-health-live-${state}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono ${c.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

export function Cell({ label, value, hint, accent, testid }) {
  return (
    <div data-testid={testid} className="px-4 py-3 bg-zinc-900/40">
      <div className="label-tiny">{label}</div>
      <div className="font-display text-2xl font-black mt-1 mono tabular-nums" style={{ color: accent || "#fafafa" }}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{hint}</div>}
    </div>
  );
}

const PILL = {
  emerald: "border-emerald-500/30 text-emerald-400 bg-emerald-500/5",
  amber: "border-amber-500/30 text-amber-400 bg-amber-500/5",
};

function Pill({ label, value, stale, accent, testid }) {
  return (
    <div data-testid={testid} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border ${PILL[accent] || "border-zinc-700 text-zinc-300 bg-zinc-900"} font-mono text-[11px]`}>
      <span className="opacity-60">{label}</span>
      <span className="tabular-nums font-semibold">{value}</span>
      {stale > 0 && <span className="text-red-400 ml-0.5">({stale})</span>}
    </div>
  );
}

export function SiteRow({ site }) {
  const totalStale = (site.would_purge_pending || 0) + (site.would_archive_ready || 0);
  const pressureColor = totalStale > 50 ? "text-red-400" : totalStale > 0 ? "text-amber-400" : "text-zinc-500";
  return (
    <Link
      data-testid={`queue-health-row-${site.slug || site.site_id}`}
      to={`/admin/sites/${site.site_id}`}
      className="block px-5 py-3 hover:bg-zinc-900/60 transition-colors group"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`dot ${site.status === "live" ? "bg-emerald-500" : "bg-zinc-600"}`} />
            <div className="text-sm text-zinc-100 truncate group-hover:text-white">{site.name || site.slug}</div>
            {site.domain && <div className="text-[11px] text-zinc-500 font-mono truncate hidden sm:block">{site.domain}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Pill testid={`row-pending-${site.slug}`} label="P" value={fmt(site.pending)} stale={site.would_purge_pending} />
          <Pill testid={`row-ready-${site.slug}`} label="R" value={fmt(site.ready)} stale={site.would_archive_ready} accent="amber" />
          <Pill testid={`row-published-${site.slug}`} label="✓" value={fmt(site.published)} accent="emerald" />
          <div className={`text-[10px] font-mono uppercase tracking-wider min-w-[60px] text-right ${pressureColor}`}>
            {totalStale === 0 ? "clean" : `${fmt(totalStale)} stale`}
          </div>
          <ChevronRight size={14} className="text-zinc-600 group-hover:text-zinc-300 transition-colors" />
        </div>
      </div>
    </Link>
  );
}

export function SavingsFooter({ data, savings }) {
  const reasons = Object.entries(savings?.by_reason || {})
    .filter(([, n]) => n > 0)
    .map(([r, n]) => `${n} ${r.replace(/_/g, " ")}`)
    .join(", ");
  return (
    <div className="px-5 py-2 border-t border-zinc-800 text-[10px] text-zinc-600 font-mono flex items-center justify-between flex-wrap gap-2">
      <span>Generated {new Date(data.generated_at).toLocaleTimeString()}</span>
      {savings && savings.total_skipped > 0 ? (
        <span data-testid="qh-savings" className="text-emerald-500">
          ✓ Pre-rewrite gate saved <span className="font-semibold">${savings.est_savings_usd.toFixed(2)}</span> today
          {" "}({savings.total_skipped} skipped: {reasons})
        </span>
      ) : (
        <span>Auto-sweep runs every 1h (pending) / 6h (ready)</span>
      )}
    </div>
  );
}
